import React, {
  type ComponentProps,
  createContext,
  type ReactNode,
  useContext,
} from 'react'

import { AnchoredOverlay } from './AnchoredOverlay'
import { Card } from './Card'
import { Type } from './Type'
import { useUniqueId } from './useUniqueId'

const PopoverContext = createContext<
  | {
      titleId: string
    }
  | undefined
>(undefined)
function usePopoverContext(componentName: string) {
  const context = useContext(PopoverContext)
  if (!context) {
    throw new Error(
      `Popover: Missing context provider. Are you using ${componentName} outside Popover?`
    )
  }
  return context
}

export function usePopoverTitleId() {
  return usePopoverContext('your custom header').titleId
}

type Props = {
  /**
   * Unique id provided to the popover. This id should also be provided to the trigger element in the following aria-attributes.
   *
   * ```
   * aria-owns={id}
   * aria-controls={id}
   * ```
   */
  id: string | number
  width?: 'standard' | 'wide'
} & ComponentProps<typeof AnchoredOverlay>

/**
 * Styled popover component. Popovers are triggered by a user clicking on an action (Button, IconButton, TextButton, etc).
 * Popovers can be dismissed by clicking on the UI target again, or by clicking off on any other part of the screen.
 *
 * This component provides additional styled `Header` and `Body` component that can be accessed like so:
 *
 * ```
 * Popover.Header
 * Popover.Body
 * ```
 *
 * You can also use your own custom content within a popover. Please check with your designer to follow appropriate spacing and sizing guidelines for custom content.
 *
 * Note about a11y for trigger elements:
 *
 * Trigger buttons/other interactive elements should have the following aria-attributes to confrom to a11y standards.
 *
 * ```
 * aria-describedby={id} // same id to pass into Popover component
 *
 * aria-owns={id} // same id to pass into Popover component
 *
 * aria-controls={<id>} // same id to pass into Popover component
 *
 * aria-expanded={<is_open>} // true / false
 *
 * ```
 */
export function Popover({
  children,
  gap = 10,
  id,
  width = 'standard',
  ...rest
}: Props) {
  const titleId = useUniqueId()

  return (
    <PopoverContext.Provider value={{ titleId }}>
      <AnchoredOverlay gap={gap} shouldLockFocus={false} {...rest}>
        <div
          aria-labelledby={titleId}
          aria-live="polite"
          id={String(id)}
          role="tooltip"
        >
          <Card
            css={{
              borderRadius: 4,
              display: 'flex',
              flexDirection: 'column',
              width: width === 'standard' ? 290 : 327,
            }}
          >
            {typeof children !== 'function' ? children : undefined}
          </Card>
        </div>
      </AnchoredOverlay>
    </PopoverContext.Provider>
  )
}

Popover.Body = function PopoverBody({
  content,
}: {
  /**
   * Any react renderable element to use for content. if using custom content please do not use custom text sizes
   * as this component wraps content with `size="S"` Text component.
   */
  content: ReactNode
}) {
  usePopoverContext('your custom body')

  return (
    <Type.Text
      overrideRootStyles={{
        padding: `0 24px 24px`,
      }}
      size="S"
    >
      {content}
    </Type.Text>
  )
}

Popover.Header = function PopoverHeader({
  title,
}: {
  /**
   * String to use for title.
   */
  title: string
}) {
  const titleId = usePopoverTitleId()

  return (
    <header id={titleId}>
      <Type.Text
        isBold
        overrideRootStyles={{
          display: 'block',
          padding: `24px 24px 8px`,
        }}
        size="M"
      >
        {title}
      </Type.Text>
    </header>
  )
}
